import { CompositionThumbnailService } from '@src/services/CreatomateService';
import { buildUploadedMediaCardTemplate } from '@src/services/videoTemplate/uploadedMediaCard';
import {
  useDatoPerson,
  useStore,
  useTemplatedPrompts,
  useUserIdentity,
} from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { AspectRatio } from '@src/types.ts/video';
import { saveCompositionTemplate } from '@src/utility/compositionSettings';
import { LogManager } from '@src/utility/Logger';
import { useFlagsCombination } from '@src/utility/useFlagsCombination';
import { observer } from 'mobx-react-lite';
import React, {
  createContext,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import type {
  Category,
  CompositionTemplate,
  ControlConfiguration,
  Subcategory,
} from './CompositionSettings/types';

export type CompositionActions = 'create' | 'update' | 'save_as';

interface CompositionContextValue {
  // Data - nested structure
  categories: Category[];
  compositions: CompositionTemplate[];

  // Category selection
  selectedCategoryId: string | null;
  setSelectedCategoryId: (id: string | null) => void;

  // Subcategory filter (for "See All" views)
  selectedSubcategoryId: string | null;
  setSelectedSubcategoryId: (subcategoryId: string | null) => void;

  // Loading state
  isLoading: boolean;
  /** New templates whose thumbnail is still being rendered. */
  pendingThumbnailIds: ReadonlySet<string>;

  // Helper methods to access nested data
  selectedCategory: Category | undefined;
  selectedSubcategory: Subcategory | undefined;
  getCompositionById: (
    compositionId: string,
  ) => CompositionTemplate | undefined;
  getCompositionWithCategoryAndSubcategoryById: (compositionId: string) => {
    category: Category | undefined;
    subcategory: Subcategory | undefined;
    composition: CompositionTemplate | undefined;
  };
  addUpdateFavoriteCompositionTemplate: (
    composition: CompositionTemplate,
    newCompositionName: string,
    updatedControls: ControlConfiguration[],
    categoryId: string,
    subcategoryId: string,
    action: CompositionActions,
    /** New templates only: called once their thumbnail is rendered. */
    onThumbnailGenerated?: (thumbnail: { id: string; url: string }) => void,
  ) => Promise<CompositionTemplate | undefined>;
  deleteComposition: (compositionId: string) => void;
}

const currentAspectConfiguration: Record<
  AspectRatio,
  keyof CompositionTemplate
> = {
  [AspectRatio.AR_16_9]: 'sixteenByNine' as keyof CompositionTemplate,
  [AspectRatio.AR_9_16]: 'nineBySixteen' as keyof CompositionTemplate,
  [AspectRatio.AR_1_1]: 'oneByOne' as keyof CompositionTemplate,
};

const CompositionContext = createContext<CompositionContextValue | undefined>(
  undefined,
);

// Module-level cache so compositions survive provider remounts (e.g. brand kit navigation).
// Keyed by isQuoteCard to separate quote card vs creator studio compositions.
const compositionCache: Record<
  string,
  { categories: Category[]; compositions: CompositionTemplate[] }
> = {};

const getCategoriesWithCompositions = async (categories: Category[]) => {
  const categoriesArray: Category[] = [];
  const compositions: CompositionTemplate[] = [];

  categories.forEach((category: Category) => {
    if (!category?.id) return;

    const subcategoriesArray: Subcategory[] = [];
    // Process subcategories
    if (Array.isArray(category.subcategories)) {
      category.subcategories.forEach((subcategory: Subcategory) => {
        if (!subcategory?.id) return;

        const compositionsArray: CompositionTemplate[] = [];

        // Process compositions
        if (Array.isArray(subcategory.compositions)) {
          subcategory.compositions.forEach((comp: CompositionTemplate) => {
            if (!comp?.id) return;

            const composition: CompositionTemplate = {
              ...comp,
              label: comp.label ?? '',
              internalName: comp.internalName ?? '',
              placement: comp.placement ?? null,
              aspectRatio: comp.aspectRatio ?? '',
              source:
                typeof comp.source === 'object' && comp.source !== null
                  ? comp.source
                  : {},
              controls: Array.isArray(comp.controls) ? comp.controls : [],
              isQuoteCard: comp.isQuoteCard ?? false,
            };
            compositionsArray.push(composition);
            // Build lookup map for O(1) access
            compositions.push(composition);
          });
        }

        subcategoriesArray.push({
          id: subcategory.id,
          label: subcategory.label ?? '',
          order: subcategory.order ?? 0,
          compositions: compositionsArray,
        });
      });
    }

    // Sort subcategories by order
    subcategoriesArray.sort((a, b) => a.order - b.order);

    categoriesArray.push({
      id: category.id,
      label: category.label ?? '',
      order: category.order ?? 0,
      subcategories: subcategoriesArray,
    });
  });
  return { categories: categoriesArray, compositions: compositions };
};

const mergeCategories = (target: Category[], source: Category[]): void => {
  for (const sourceCategory of source) {
    const targetCategoryIndex = target.findIndex(
      c => c.id === sourceCategory.id,
    );

    if (targetCategoryIndex !== -1) {
      for (const sourceSubcategory of sourceCategory.subcategories) {
        const targetSubcategoryIndex = target[
          targetCategoryIndex
        ].subcategories.findIndex(s => s.id === sourceSubcategory.id);
        if (targetSubcategoryIndex !== -1) {
          // A global preset that is also a workspace template is already
          // listed; a second copy duplicates its React key, which strands
          // stale cards in the grid on a tab switch (PLAT-11732)
          const targetCompositions =
            target[targetCategoryIndex].subcategories[targetSubcategoryIndex]
              .compositions;
          const listedIds = new Set(targetCompositions.map(c => c.id));
          targetCompositions.push(
            ...sourceSubcategory.compositions.filter(c => !listedIds.has(c.id)),
          );
        } else {
          target[targetCategoryIndex].subcategories.push(sourceSubcategory);
        }
      }
      // Sort subcategories after merging
      target[targetCategoryIndex].subcategories.sort(
        (a, b) => a.order - b.order,
      );
    } else {
      // Add new category
      target.push(sourceCategory);
    }
  }
};

export const useCompositionContext = () => {
  const context = useContext(CompositionContext);
  if (!context) {
    throw new Error(
      'useCompositionContext must be used within CompositionProvider',
    );
  }
  return context;
};

interface Props {
  children: ReactNode;
  isQuoteCard?: boolean;
}

export const CompositionProvider: React.FC<Props> = observer(props => {
  const { children, isQuoteCard = false } = props;
  const logger = LogManager.getLogger('component:CompositionProvider');
  const { datoClientStore } = useStore();
  const datoPerson = useDatoPerson();
  const userIdentity = useUserIdentity();
  const videoCreator = useVideoCreatorStore();
  // Quote cards are always 1:1 aspect ratio, regardless of video aspect ratio
  const currentAspectRatio = isQuoteCard
    ? '1:1'
    : videoCreator.getCurrentAspectRatio();
  const {
    creatorStudioEnableDesignTabs,
    creatorStudioEnableCompositionsTab,
    enableShareableImageCompositions,
  } = useFlagsCombination(userIdentity.currentRole);

  const templatedPromptsStore = useTemplatedPrompts();
  const isTemplatedPromptsInitialized = templatedPromptsStore.isInitialized;
  const whitelistedCategoriesIds = templatedPromptsStore.graphicCategoriesIds;
  const whitelistedSubcategoriesIds =
    templatedPromptsStore.graphicSubcategoriesIds;

  // State — initialize from module-level cache to avoid loading spinner on remount
  const cacheKey = isQuoteCard ? 'quoteCard' : 'creatorStudio';
  const cached = compositionCache[cacheKey];
  const [categories, setCategories] = useState<Category[]>(
    cached?.categories ?? [],
  );
  const [compositions, setCompositions] = useState<CompositionTemplate[]>(
    cached?.compositions ?? [],
  );

  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(
    null,
  );
  const [selectedSubcategoryId, setSelectedSubcategoryId] = useState<
    string | null
  >(null);
  const [isLoading, setIsLoading] = useState(!cached); // Skip loading if cache exists
  const [pendingThumbnailIds, setPendingThumbnailIds] = useState<
    ReadonlySet<string>
  >(() => new Set());
  const hasFetchedRef = useRef(!!cached);

  // Determine if we should fetch compositions:
  // - For Creator Studio: need both design tabs and compositions tab enabled
  // - For Content Studio (isQuoteCard=true): need enableShareableImageCompositions enabled
  const shouldFetchCompositions =
    (creatorStudioEnableDesignTabs && creatorStudioEnableCompositionsTab) ||
    (isQuoteCard && enableShareableImageCompositions);

  // Fetch compositions
  useEffect(() => {
    const fetchCompositions = async () => {
      // If we shouldn't fetch, set loading to false and return
      if (!shouldFetchCompositions) {
        setIsLoading(false);
        return;
      }

      // Wait for organization to be available
      if (!videoCreator.organization) {
        return; // Keep isLoading true until organization is available
      }

      // Wait for templatedPromptsStore to load the category whitelists.
      // Without this, an in-app navigation (e.g. Home → Recent Stories) can
      // race the fetch ahead of `initTemplatedPrompts`, producing an empty
      // result that gets cached and never refreshed.
      if (!isTemplatedPromptsInitialized) {
        return;
      }

      // Only show loading spinner on initial fetch, not on re-fetches.
      // This prevents unmounting child components (e.g. QuoteCard renderers)
      // that are expensive to re-initialize.
      if (!hasFetchedRef.current) {
        setIsLoading(true);
      }

      try {
        const allCategories: Category[] = [];
        const allCompositions: CompositionTemplate[] = [];

        // Fetch showcase compositions (Workspace Templates)
        const showcaseCompositionTemplateIds = isQuoteCard
          ? videoCreator.organization?.quoteCardsBranding?.map(c => c.id)
          : videoCreator.organization?.compositionsBranding?.map(c => c.id);
        if (
          showcaseCompositionTemplateIds &&
          showcaseCompositionTemplateIds.length > 0
        ) {
          const showcaseData =
            await datoClientStore.compositionRepository.getCategoriesWithCompositionsByCompositionIds(
              showcaseCompositionTemplateIds,
              whitelistedCategoriesIds,
              whitelistedSubcategoriesIds,
              { isQuoteCard },
            );

          if (Array.isArray(showcaseData)) {
            const {
              categories: showcaseCategories,
              compositions: showcaseCompositions,
            } = await getCategoriesWithCompositions(showcaseData);

            allCategories.push(...showcaseCategories);
            allCompositions.push(...showcaseCompositions);
          }
        }

        // For quote cards, if no Workspace Templates are configured, fetch the Default Quote Card directly

        const globalData =
          await datoClientStore.compositionRepository.getAllCategoriesWithCompositions(
            whitelistedCategoriesIds,
            whitelistedSubcategoriesIds,
            { isQuoteCard },
          );
        if (Array.isArray(globalData)) {
          const {
            categories: globalCategories,
            compositions: globalCompositions,
          } = await getCategoriesWithCompositions(globalData);

          mergeCategories(allCategories, globalCategories);
          allCompositions.push(...globalCompositions);
        }
        // Sort categories by order

        allCategories.sort((a, b) => a.order - b.order);

        setCategories(allCategories);
        setCompositions(allCompositions);
        hasFetchedRef.current = true;
        compositionCache[cacheKey] = {
          categories: allCategories,
          compositions: allCompositions,
        };
      } catch (error) {
        logger.warn('Failed to fetch compositions', { error });
      } finally {
        setIsLoading(false);
      }
    };

    fetchCompositions();
  }, [
    shouldFetchCompositions,
    datoClientStore.compositionRepository,
    videoCreator.organization,
    isQuoteCard,
    isTemplatedPromptsInitialized,
    whitelistedCategoriesIds,
    whitelistedSubcategoriesIds,
  ]);

  const { categoriesFiltered, compositionsFiltered } = useMemo(() => {
    if (!currentAspectRatio) {
      return {
        categoriesFiltered: categories,
        compositionsFiltered: compositions,
      };
    }

    // Hoist the aspect config key lookup to avoid repeated dictionary access
    const aspectConfigKey = currentAspectConfiguration[currentAspectRatio];

    const hasValidAspectConfigInline = (composition: CompositionTemplate) => {
      if (!aspectConfigKey) return false;
      const aspectConfig = composition?.[aspectConfigKey] || {};
      return (
        !!aspectConfig && Object.values(aspectConfig).some(value => !!value)
      );
    };

    const categoriesFiltered = categories
      .map(category => ({
        ...category,
        subcategories: category.subcategories
          .map(subcategory => ({
            ...subcategory,
            compositions: subcategory.compositions.filter(
              hasValidAspectConfigInline,
            ),
          }))
          .filter(subcategory => subcategory.compositions.length > 0),
      }))
      .filter(category => category.subcategories.length > 0);

    const compositionsFiltered = compositions.filter(
      hasValidAspectConfigInline,
    );

    return { categoriesFiltered, compositionsFiltered };
  }, [currentAspectRatio, categories, compositions]);

  // Reset selection when aspect ratio changes
  useEffect(() => {
    setSelectedCategoryId(null);
    setSelectedSubcategoryId(null);
  }, [currentAspectRatio]);

  // Helper methods
  const selectedCategory = useMemo(() => {
    if (!selectedCategoryId) return undefined;

    return categoriesFiltered.find(cat => cat.id === selectedCategoryId);
  }, [selectedCategoryId, categoriesFiltered]);

  const selectedSubcategory = useMemo(() => {
    if (!selectedSubcategoryId) return undefined;
    for (const category of categoriesFiltered) {
      const subcategory = category.subcategories.find(
        sub => sub.id === selectedSubcategoryId,
      );
      if (subcategory) return subcategory;
    }
    return undefined;
  }, [selectedSubcategoryId, categoriesFiltered]);

  const getCompositionById = useCallback(
    (compositionId: string) => {
      return (
        compositions.find(comp => comp.id === compositionId) ??
        // PLAT-11732 — a card built from the creator's own upload has no
        // record; its id carries the card
        buildUploadedMediaCardTemplate(compositionId)
      );
    },
    [compositions],
  );

  const getCompositionWithCategoryAndSubcategoryById = useCallback(
    (compositionId: string) => {
      const composition = getCompositionById(compositionId);
      if (!composition)
        return {
          category: undefined,
          subcategory: undefined,
          composition: undefined,
        };
      for (const category of categoriesFiltered) {
        const subcategory = category.subcategories.find(sub =>
          sub.compositions.some(comp => comp.id === compositionId),
        );

        if (subcategory) return { category, subcategory, composition };
      }
      logger.warn('Composition not found', { compositionId });
      return {
        category: undefined,
        subcategory: undefined,
        composition: composition,
      };
    },
    [getCompositionById, categoriesFiltered],
  );

  const addUpdateFavoriteCompositionTemplate = useCallback(
    async (
      composition: CompositionTemplate,
      newCompositionName: string,
      updatedControls: ControlConfiguration[],
      categoryId: string,
      subcategoryId: string,
      action: CompositionActions,
      onThumbnailGenerated?: (thumbnail: { id: string; url: string }) => void,
    ) => {
      const thumbnailService = new CompositionThumbnailService(videoCreator);
      const response = await saveCompositionTemplate(
        composition,
        newCompositionName,
        updatedControls as ControlConfiguration[],
        videoCreator.organization,
        datoClientStore.compositionRepository,
        subcategoryId,
        datoPerson.person?.id || '',
        action,
      );
      if (!response) {
        videoCreator.showToast({
          state: 'error',
          message: 'Error occurred in saving template',
          delay: 3000,
        });
        return;
      }

      // The repo links a new template to the showcase in Dato; mirror that on
      // the loaded organization, which the fetch lists workspace templates
      // from, or the template drops out on the next refetch (and an
      // albumRepository.update(organization) writes the stale list back).
      // Set the field rather than replace the organization: a new reference
      // refetches before the repo's unawaited subcategory link lands.
      const organization = videoCreator.organization;
      if (action !== 'update' && organization) {
        const field = composition.isQuoteCard
          ? 'quoteCardsBranding'
          : 'compositionsBranding';
        const linked = organization[field] ?? [];
        if (!linked.some(c => c.id === response.id)) {
          organization[field] = [...linked, response];
        }
      }

      saveNewCompositionInStore(
        // For 'update' (Save Changes) the repo returns thumbnail: undefined, so
        // the store keeps the listed thumbnail. Not `composition.thumbnail`:
        // after Save New Template that is the previous template's (PLAT-11977).
        response,
        categoryId,
        subcategoryId,
        setCompositions,
        setCategories,
        action,
        cacheKey,
      );

      videoCreator.showToast({
        state: 'success',
        message:
          action === 'update'
            ? 'Template updated successfully'
            : 'Template saved successfully',
        delay: 3000,
      });

      // For new templates (create/save_as) asynchronously generate + set a thumbnail.
      // For 'update' (Save Changes) we intentionally skip regeneration so the existing
      // thumbnail is left untouched — in the UI and in DatoCMS.
      if (action !== 'update') {
        setPendingThumbnailIds(ids => new Set(ids).add(response.id));
        thumbnailService
          .generateCompositionThumbnail(response, !isQuoteCard)
          .then(thumbnailResponse => {
            if (thumbnailResponse !== undefined) {
              saveNewCompositionInStore(
                { ...response, thumbnail: thumbnailResponse },
                categoryId,
                subcategoryId,
                setCompositions,
                setCategories,
                'update',
                cacheKey,
              );
              onThumbnailGenerated?.(thumbnailResponse);
            }
          })
          .finally(() =>
            setPendingThumbnailIds(ids => {
              const next = new Set(ids);
              next.delete(response.id);
              return next;
            }),
          );
      }

      // The repo drops the thumbnail; callers save again from this record, and
      // Dato rejects a create without one.
      return { ...response, thumbnail: composition.thumbnail };
    },
    [
      datoClientStore.compositionRepository,
      videoCreator.organization,
      datoClientStore,
      datoPerson.person,
    ],
  );

  const deleteComposition = useCallback(
    (compositionId: string) => {
      if (!datoPerson.person?.id) return;
      const composition = getCompositionById(compositionId);
      if (!composition || composition.isGlobalPreset) return;
      datoClientStore.compositionRepository.deleteCustomCompositionTemplate(
        compositionId,
        datoPerson.person?.id || '',
      );

      deleteCompositionInStore(
        compositionId,
        setCompositions,
        setCategories,
        cacheKey,
      );
    },
    [
      datoClientStore.compositionRepository,
      datoPerson.person?.id,
      setCompositions,
      setCategories,
      getCompositionById,
    ],
  );

  const value: CompositionContextValue = {
    categories: categoriesFiltered,
    compositions: compositionsFiltered,
    selectedCategoryId,
    setSelectedCategoryId,
    selectedSubcategoryId,
    setSelectedSubcategoryId,
    isLoading,
    pendingThumbnailIds,
    selectedCategory,
    selectedSubcategory,
    getCompositionById,
    getCompositionWithCategoryAndSubcategoryById,
    addUpdateFavoriteCompositionTemplate,
    deleteComposition,
  };

  return (
    <CompositionContext.Provider value={value}>
      {children}
    </CompositionContext.Provider>
  );
});

export function saveNewCompositionInStore(
  newComposition: CompositionTemplate,
  categoryId: string,
  subcategoryId: string,
  setCompositions: Dispatch<SetStateAction<CompositionTemplate[]>>,
  setCategories: Dispatch<SetStateAction<Category[]>>,
  action: 'create' | 'update' | 'save_as',
  cacheKey?: string,
) {
  // Invalidate module-level cache so next remount picks up the change
  if (cacheKey) delete compositionCache[cacheKey];
  const isNew = action === 'create' || action === 'save_as';
  // An update without a thumbnail keeps the listed one (PLAT-11977).
  const replace = (comp: CompositionTemplate) =>
    comp.id !== newComposition.id
      ? comp
      : newComposition.thumbnail
        ? newComposition
        : { ...newComposition, thumbnail: comp.thumbnail };

  // Updaters must be pure — React runs them twice in development. The old
  // in-place `unshift` into the subcategory's array inserted a new template
  // twice, and the duplicate key broke deleting it (PLAT-11732). A template
  // already present is never added again either.
  setCompositions(prev =>
    isNew
      ? prev.some(comp => comp.id === newComposition.id)
        ? prev
        : [...prev, newComposition]
      : prev.map(replace),
  );
  setCategories(prev =>
    prev.map(category =>
      category.id !== categoryId
        ? category
        : {
            ...category,
            subcategories: category.subcategories.map(subcategory => {
              if (subcategory.id !== subcategoryId) return subcategory;
              const { compositions } = subcategory;
              if (isNew) {
                return compositions.some(comp => comp.id === newComposition.id)
                  ? subcategory
                  : {
                      ...subcategory,
                      compositions: [newComposition, ...compositions],
                    };
              }
              return {
                ...subcategory,
                compositions: compositions.map(replace),
              };
            }),
          },
    ),
  );
}

function deleteCompositionInStore(
  compositionId: string,
  setCompositions: Dispatch<SetStateAction<CompositionTemplate[]>>,
  setCategories: Dispatch<SetStateAction<Category[]>>,
  cacheKey?: string,
) {
  // Invalidate module-level cache so next remount picks up the change
  if (cacheKey) delete compositionCache[cacheKey];
  setCompositions(prev => prev.filter(comp => comp.id !== compositionId));
  setCategories(prev =>
    prev.map(cat => ({
      ...cat,
      subcategories: cat.subcategories.map(sub => ({
        ...sub,
        compositions: sub.compositions.filter(
          comp => comp.id !== compositionId,
        ),
      })),
    })),
  );
}
